import React, { useEffect, useRef, useState } from "react";
import "./tournament-round-hole-assignment-team-list.scss";
import {
  defaultGapping,
  defaultLayouts,
  defaultPadding,
  golfstatusSpacing,
  GSActionBar,
  GSEmptyList,
  GSInput,
  GSItemList,
  useMediaQuery,
} from "golfstatus_react_components";
import { faSearch, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { useMatch, useNavigate } from "react-router-dom";
import TournamentRoundHoleAssignmentTeamItem from "./tournament-round-hole-assignment-team-item";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCurrentTeam,
  selectLoading,
  selectTournamentTeams,
  setCurrentTeam,
} from "../../../../reducers/teamsSlice";
import {
  saveRoundHoleAssignment,
  selectCurrentHoleAssignment,
  selectCurrentRound,
  selectCurrentRoundHoleAssignments,
  selectLinkedRoundHoleAssignments,
  selectRecentlyUnassignedTeams,
  setPendingHoleAssignment,
  setPendingTeams,
  setRecentlyAssignedTeams,
  setRecentlyUnassignedTeams,
  setCurrentHoleAssignment,
} from "../../../../reducers/roundSlice";
import { selectTournamentFlights } from "../../../../reducers/flightSlice";
import { filterUnassignedTeams, getTeamFlight } from "../../../../hooks/round/roundHoleAssignmentHooks";

const TournamentRoundHoleAssignmentTeamList = (props) => {
  const { style } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const match = useMatch("/:tournamentID/rounds/:roundID/hole-assignments");
  const { isPhone } = useMediaQuery();

  // selectors
  const currentRound = useSelector(selectCurrentRound);
  const currentHoleAssignment = useSelector(selectCurrentHoleAssignment);
  const recentlyUnassignedTeams = useSelector(selectRecentlyUnassignedTeams);
  const linkedRoundHoleAssignments = useSelector(
    selectLinkedRoundHoleAssignments,
  );
  const currentRoundHoleAssignments = useSelector(
    selectCurrentRoundHoleAssignments,
  );
  const currentTournamentTeam = useSelector(selectCurrentTeam);
  const flights = useSelector(selectTournamentFlights);
  const tournamentTeams = useSelector(selectTournamentTeams);
  const loading = useSelector(selectLoading);

  // local state
  const [search, setSearch] = useState("");
  const listRef = useRef(null);

  const getTournamentTeamSearchString = (team) => {
    let searchString = "";

    const teamName =
      team.name?.toLowerCase() || team.contactName?.toLowerCase();
    const playerNames = team.players.map((p) => p.name.toLowerCase()).join(" ");
    if (searchString.length > 0) {
      searchString += " ";
    }
    searchString += teamName + " " + playerNames;
    return searchString;
  };

  //a round opens at the top rather than wherever the last one was left off. the
  //teams list is the one that scrolls, and it is this element's own child rather
  //than one of the lists the items and empty message bring with them.
  useEffect(() => {
    const scroller = listRef.current?.querySelector?.(":scope > gs-item-list");

    if (scroller) {
      scroller.scrollTop = 0;
    }
  }, [currentRound?.id]);

  const applySearchTo = (unassignedTeams) => {
    if (search === "") {
      return unassignedTeams;
    }

    let searchResults = [];
    // search by team name or player name
    searchResults = unassignedTeams?.filter?.((ut) => {
      let searchString = getTournamentTeamSearchString(ut);
      return searchString.includes(search.toLowerCase());
    });

    return searchResults;
  };

  const getUnassignedTeams = () => {
    //a team can only play a round number once, so an assignment in a linked
    //round takes it out of this round's list too
    const assignments = [
      ...currentRoundHoleAssignments,
      ...linkedRoundHoleAssignments,
    ];

    let unassigned = filterUnassignedTeams(tournamentTeams, assignments)
      .filter((team) => team.disqualified === false)
      .map((team) => ({ ...team, flight: getTeamFlight(team, flights) }))
      .sort((a, b) => a.mainFlightPosition - b.mainFlightPosition);

    return applySearchTo(unassigned);
  };

  const getEmptySearchMessage = () => {
    return (
      <GSEmptyList
        title="We couldn’t find who you’re looking for…"
        detail="Try changing the search term or review the players and teams list."
        listType={"mobile-vertical"}
        actions={[
          {
            title: "Clear Search",
            onClick: () => {
              setSearch("");
            },
            type: "full-width light-grey",
          },
          {
            title: "View Players & Teams",
            onClick: () => navigate(`/${match?.params?.tournamentID}/teams`),
            type: "full-width black no-wrap",
          },
        ]}
      />
    );
  };

  const hasAssignedTeam = (holeAssignment) => {
    return holeAssignment?.roundHoleAssignmentTournamentTeams?.some(
      (rhatt) => rhatt?.grouping === holeAssignment?.grouping,
    );
  };

  //the selection is a snapshot taken when the spot was clicked, so the live
  //record is what says whether it still holds a team
  const getSelectedAssignment = () => {
    return {
      ...currentRoundHoleAssignments?.find?.(
        (rha) => rha?.id === currentHoleAssignment?.id,
      ),
      grouping: currentHoleAssignment?.grouping,
    };
  };

  const getAssignment = (item) => {
    return (
      <TournamentRoundHoleAssignmentTeamItem
        team={{ ...item }}
        assigned={false}
        available={currentHoleAssignment?.id !== undefined}
        selected={currentTournamentTeam?.id === item?.id}
        swappable={hasAssignedTeam(getSelectedAssignment())}
        justUnassigned={recentlyUnassignedTeams?.includes?.(item?.id)}
      />
    );
  };

  const selectUnassignedTeam = (team) => {
    if (currentHoleAssignment?.id) {
      if (hasAssignedTeam(getSelectedAssignment())) {
        dispatch(setCurrentHoleAssignment({}));
        dispatch(setCurrentTeam(team));
      } else {
        let updatedHoleAssignment = {
          ...currentHoleAssignment,
          roundHoleAssignmentTournamentTeams: [
            ...currentHoleAssignment.roundHoleAssignmentTournamentTeams,
            {
              tournamentTeamKey: team.id,
              roundHoleAssignmentKey: currentHoleAssignment.id,
              grouping: currentHoleAssignment.grouping,
            },
          ],
        };
        dispatch(setPendingTeams([team?.id]));
        dispatch(setRecentlyAssignedTeams([team?.id]));
        dispatch(setRecentlyUnassignedTeams([]));
        dispatch(
          setPendingHoleAssignment({
            id: currentHoleAssignment?.id,
            grouping: currentHoleAssignment?.grouping,
          }),
        );
        dispatch(saveRoundHoleAssignment(updatedHoleAssignment));
      }
    } else {
      if (team?.id === currentTournamentTeam?.id) {
        dispatch(setCurrentTeam({}));
      } else {
        dispatch(setCurrentTeam(team));
      }
    }
  };

  const unassignedTeams = getUnassignedTeams();
  return (
    <tournament-round-hole-assignment-team-list ref={listRef} style={style}>
      <div
        style={{
          ...defaultPadding.xLargePad,
          ...defaultLayouts.fullWidth,
          ...defaultLayouts.vStack,
          ...defaultGapping.xLargeGap,
          flex: "0 0 auto",
        }}
      >
        <GSActionBar
          type="action-bar-header"
          header={`Unassigned (${unassignedTeams?.length ?? 0})`}
        />
        {!isPhone() && (
          <GSInput
            placeholder="Search Players & Teams…"
            leftIcon={faSearch}
            rightIcon={faTimesCircle}
            rightIconClick={() => {
              setSearch("");
            }}
            textValue={search}
            onChange={(e) => {
              setSearch(e?.target?.value);
            }}
          ></GSInput>
        )}
      </div>
      <GSItemList
        style={
          isPhone()
            ? {
                ...defaultLayouts.fullWidth,
                ...defaultPadding.xLargePad,
                paddingTop: "0",
                paddingBottom: golfstatusSpacing.mediumPad,
                overflow: "auto",
              }
            : {
                ...defaultLayouts.fullWidth,
                ...defaultPadding.xLargePad,
                boxSizing: "border-box",
                minHeight: 0,
                flex: "1 1 auto",
                overflow: "auto",
              }
        }
        listStyle={{ ...defaultGapping.mediumGap }}
        type={isPhone() ? "horizontal" : "vertical"}
        items={unassignedTeams}
        listItem={(item) => getAssignment(item)}
        itemSelected={(item) => selectUnassignedTeam({ ...item })}
        emptyMessage={getEmptySearchMessage()}
        loading={loading?.includes("getTournamentTeams")}
        loadingMainText="Loading teams..."
      />
    </tournament-round-hole-assignment-team-list>
  );
};

export default TournamentRoundHoleAssignmentTeamList;
